/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));

        // Parse and store variant Amazon URLs
        this.variantAmazonUrls = this.parseVariantAmazonUrls();
        this.amazonButton = this.querySelector('.amazon-channel-btn');

        // Debug logging
        if (Object.keys(this.variantAmazonUrls).length > 0) {
        //  console.log('✓ Amazon URLs loaded:', this.variantAmazonUrls);
        }

        // Listen for variant changes - try multiple event names
        this.setupVariantChangeListeners();

        if (this.dataset.showQuantitySelector) {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            this.lineItemChangeListener = this.lineItemChangeListener
              || this.handleLineItemChange.bind(this);
            document.addEventListener('on:line-item:change', (evt) => {
              this.lineItemChangeListener(evt);
            });
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
      }
    }

    /**
     * Setup variant change event listeners for multiple event names
     */
    setupVariantChangeListeners() {
      const eventNames = [
        'on:variant:change',
        'variant:change',
        'variant:selected',
        'productVariantChange'
      ];

      eventNames.forEach(eventName => {
        document.addEventListener(eventName, (evt) => {
          this.handleVariantChangeEvent(evt);
        });
      });

      // Also listen for direct form input changes (select elements)
      const variantSelect = this.querySelector('select[name="id"]');
      if (variantSelect) {
        variantSelect.addEventListener('change', (evt) => {
          const variantId = evt.target.value;
          if (variantId) {
            this.updateAmazonButton(variantId);
          }
        });
      }
    }

    /**
     * Handle variant change event from any source
     */
    handleVariantChangeEvent(evt) {
      let variantId = null;

      // Try different ways to get variant ID from event detail
      if (evt.detail) {
        if (evt.detail.variantId) {
          variantId = evt.detail.variantId.toString();
        } else if (evt.detail.id) {
          variantId = evt.detail.id.toString();
        } else if (evt.detail.variant && evt.detail.variant.id) {
          variantId = evt.detail.variant.id.toString();
        }
      }

      if (variantId) {
        this.updateAmazonButton(variantId);
        // Also update quantity limits
        this.setMaximumInputQuantity();
      }
    }

    /**
     * Update Amazon button for specific variant
     */
    updateAmazonButton(variantId) {
      if (!this.amazonButton) {
        return;
      }

      const newUrl = this.variantAmazonUrls[variantId];

      if (newUrl) {
        this.amazonButton.href = newUrl;
     //   console.log('✓ Amazon button updated to variant:', variantId, 'URL:', newUrl);
      } else {
      //  console.log('No Amazon URL for variant:', variantId);
      }
    }

    disconnectedCallback() {
      if (this.lineItemChangeListener) {
        document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
      }
    }

    /**
     * Parses variant Amazon URLs from data-amazon-urls attribute
     */
    parseVariantAmazonUrls() {
      const urlsData = this.getAttribute('data-amazon-urls');
      if (!urlsData) return {};

      try {
        const parsed = JSON.parse(urlsData);
        return parsed || {};
      } catch (e) {
    //    console.warn('Failed to parse Amazon URLs:', e, 'Data was:', urlsData);
        return {};
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const variantId = Number(this.form.querySelector('[name="id"]').value);
            const inventoryJson = JSON.parse(dataEl.textContent);
            const inventory = inventoryJson.find((item) => item.id === variantId);

            if (inventory && inventory.inventory_management && inventory.inventory_management.length !== 0 && inventory.inventory_quantity > 0 && inventory.inventory_policy !== 'continue') {
              quantityInput.max = inventory.inventory_quantity;

              const currentQuantity = Number(quantityInput.value);
              if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                quantityInput.value = quantityInput.max;
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Handles quantity button state
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          if (minusButton && plusButton) {
            plusButton.addEventListener('click', () => {
              ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
            });

            minusButton.addEventListener('click', () => {
              ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
            });

            ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
          }
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    /**
     * Handles line item change events
     */
    handleLineItemChange(evt) {
      if (evt.detail && evt.detail.variantId && evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    /**
     * Handles quantity change
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.value);
    }

    /**
     * Handles form submission
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          ProductForm.updateCartIcon(data);

          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
                  || theme.settings.afterAtc === 'drawer')
              );

              if (quickOrderSections.length) {
                quickOrderSections.forEach((quickOrderSection) => {
                  const sectionEl = document.getElementById(quickOrderSection.id);
                  if (!sectionEl) return;

                  const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                  el.innerHTML = ProductForm.getElementHTML(
                    data.sections[quickOrderSection.section],
                    quickOrderSection.selector
                  );
                });
              }
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refreshCartItems();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        let newQuantity = 1;

        if (itemInOldCart) {
          newQuantity = (itemInOldCart.quantity === data.quantity)
            ? itemInOldCart.quantity : data.quantity;
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));

          if (this.quantitySelector) {
            this.setAttribute('data-show-quantity-selector', 'true');
            this.quantityInput.value = newQuantity;
            this.quantitySelector.currentQty = this.quantityInput.dataset.initialValue;
          }
        }
      } catch (error) {
        console.log(error);
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    updateQuantityState(newQuantity) {
      if (this.quantityInput) {
        this.quantityInput.value = newQuantity;
        if (this.quantitySelector) {
          this.quantitySelector.currentQty = newQuantity;
        }
        this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
      }
    }

    async updateQuantity(quantity) {
      if (this.quantitySelector) {
        this.quantitySelector.setAttribute('aria-disabled', 'true');
      }
      const lineErrors = this.querySelector('.js-product-form-errors');

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      fetchRequestOpts.body = JSON.stringify({
        id: this.dataset.variantId,
        quantity
      });

      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        if (lineErrors) {
          lineErrors.innerHTML = '';
          lineErrors.hidden = true;
        }
      } catch (error) {
        if (lineErrors) {
          if (/^[0-9]+$/.test(error.message)) {
            lineErrors.textContent = theme.strings.cartError;
          } else {
            lineErrors.textContent = error.message;
          }
          lineErrors.hidden = false;
        }
        console.log(error);

        const oldQuantity = parseInt(this.quantityInput.dataset.initialValue, 10);
        this.updateQuantityState(oldQuantity);
      } finally {
        this.updateQuantityState(parseInt(quantity, 10));
        if (this.quantitySelector) {
          this.quantitySelector.removeAttribute('aria-disabled');
        }
        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      }
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}